import { WritableSignal, signal, untracked } from '@angular/core';
import { ChartOpts } from '@features/social-analysis/models/IChart';
import { Dimension } from '@features/social-analysis/models/IDimensions';
import { ChartState } from '@features/social-analysis/social-shared/store/chart.store';
import { ArrayUtils } from '@shared/utils/Arrays';
// import ARIMAPromise from 'arima/async';
import { TimeFields } from '@core/models/ApiFields/time-fields.enums';
import { IData } from '@core/models/IApi';
import { OldFilterOpt } from '@features/social-analysis/models/IFilters';
import dayjs from 'dayjs';
import { ChartType } from 'ng-apexcharts';
import { emptyChartOpts } from '../config/chart-empty-opts';
import { loadingChartOpts } from '../config/chart-loading-data';
import { lineChartOptions, yaxisOpts } from '../config/line-bar-chart-opts';

type Kpis = {
    dim: Dimension;
    fixedDim: Dimension;
    metrics: Dimension[];
};

abstract class ChartOptsFormatter<T = IData> {
    protected tempOpts: ChartOpts = JSON.parse(JSON.stringify(lineChartOptions));

    compareData: WritableSignal<T[]> = signal([]);
    isForecastDisplayed: WritableSignal<boolean> = signal(false);
    isCompareOn: WritableSignal<boolean> = signal(false);

    /* Set options */
    getLoadingChart() {
        return JSON.parse(JSON.stringify(loadingChartOpts));
    }

    getEmptyChart() {
        return JSON.parse(JSON.stringify(emptyChartOpts));
    }

    abstract getChartOpts(state: ChartState<T>, data: T[]): any;

    protected abstract setXaxis(data: T[], { dim }: { dim: Dimension }): void;

    protected validateData(dim: Dimension, mets: Dimension[], data?: T[]) {
        return dim && mets.length && data?.length;
    }

    protected getKpis(state: ChartState<T>) {
        const dim: Dimension = untracked(state.dimensions).filter(dim => !dim.fixed)[0];
        const fixedDim: Dimension = untracked(state.dimensions).filter(dim => dim.fixed)[0];
        const metrics: Dimension[] = untracked(state.metrics);
        return { dim, fixedDim, metrics };
    }

    protected setChartType(chartType = 'bar') {
        this.tempOpts.chart.type = chartType as ChartType;
    }

    // X AXIS

    protected getXaxisType(dim: OldFilterOpt) {
        return dim.type === 'line' ? ('datetime' as const) : ('category' as const);
    }

    protected shouldBeWide(dim: Dimension) {
        if (!dim?.value) return;
        const dimension = dim.value;
        return dimension === 'category_name' || dimension === 'city' || dimension === 'country';
    }

    // Y AXIS

    protected setYaxisNames(metrics: Dimension[], yaxisOpts: Record<string, any>[]) {
        metrics.forEach((met, i) => this.setYaxisName(i, met, yaxisOpts[i]));
    }

    protected setYaxisName(index: number, kpi: OldFilterOpt, options?: any) {
        const yaxis = this.tempOpts.yaxis;
        if (Array.isArray(yaxis)) {
            const elOpts = options || yaxis[index];
            const newYaxis = {
                ...elOpts,
                title: { ...elOpts.title, text: kpi.display },
                seriesName: kpi.value,
            };

            this.tempOpts.yaxis = [...yaxis, newYaxis];
        } else {
            const newYaxis = {
                ...yaxis,
                title: { ...yaxis.title, text: kpi.display },
                seriesName: kpi.value,
            };

            this.tempOpts.yaxis = newYaxis;
        }
    }

    protected mapArrayFromRawData(
        data: IData[],
        dim: Dimension,
        met: Dimension
    ): { x: any; y: number | null }[] {
        const result = this.tempOpts.xaxis.categories.map((cat: string | number) => ({
            x: cat,
            y: this.getYvalue(data, dim.value, met.value, cat),
        }));

        return result;
    }

    getYvalue(data: IData[], dim: string, met: string, cat: string | number) {
        const val = data.find(e => e[dim] === cat)?.[met];
        return val === undefined ? null : val;
    }

    // Others

    moveToolbarFor1Kpi() {
        this.tempOpts.chart = {
            ...this.tempOpts.chart,
            toolbar: {
                offsetX: 0,
                offsetY: -4,
            },
        };
    }

    moveToolbarFor2Kpis() {
        this.tempOpts.chart = {
            ...this.tempOpts.chart,
            toolbar: {
                offsetX: -36,
                offsetY: -4,
            },
        };
    }
}

export class SingleDimChartFormatter extends ChartOptsFormatter {
    getChartOpts(state: ChartState, data: IData[]) {
        const kpis = this.getKpis(state);

        const isValid = this.validateData(kpis.dim, kpis.metrics, data);
        if (!isValid) return this.getEmptyChart();

        return this.setChartOpts(kpis, this.cleanData(data!, kpis.dim, kpis.metrics));
    }

    private cleanData(data: IData[], dim: Dimension, mets: Dimension[]) {
        let result = data;
        if (dim.value === 'weekday') result = this.sortDataIfWeekday(data);
        if (dim.type === 'bar') result = this.removeEmptySegments(mets, result);

        return result;
    }

    protected sortDataIfWeekday(data: IData[]) {
        return ArrayUtils.sortDataByWeekday(data);
    }

    protected removeEmptySegments(mets: Dimension[], data: IData[]) {
        return data.reduce<IData[]>( // skip dates/categories with no data
            (acc, curr) =>
                typeof curr[mets[0]?.value] === 'undefined' &&
                typeof curr[mets[1]?.value] === 'undefined'
                    ? acc
                    : [...acc, curr],
            []
        );
    }

    protected setChartOpts(kpis: Kpis, data: IData[]) {
        this.tempOpts = JSON.parse(JSON.stringify(lineChartOptions));
        this.setXaxis(data, kpis);
        this.setChartType(kpis.dim.type);
        this.setYaxisNames(kpis.metrics, yaxisOpts);
        this.setYaxisSeries(data, kpis);

        if (kpis.metrics.length === 1) {
            this.moveToolbarFor1Kpi();
        } else if (kpis.metrics.length === 2) {
            this.moveToolbarFor2Kpis();
        }

        return this.tempOpts;
    }

    protected setYaxisSeries(data: IData[], kpis: Kpis) {
        const series = kpis.metrics.map(met => ({
            name: met.display || '',
            data: this.mapArrayFromRawData(data as IData[], kpis.dim, met),
        }));

        this.tempOpts.series = series;
    }

    protected setXaxis(data: IData[], { dim }: { dim: Dimension }) {
        this.tempOpts.xaxis = {
            ...this.tempOpts.xaxis,
            type: this.getXaxisType(dim),
            labels: {
                hideOverlappingLabels: !this.shouldBeWide(dim),
            },
            categories: data?.map(item => item[dim.value]) || [],
        };
    }
}

export class InsChartOptsFormatter extends ChartOptsFormatter<IData[]> {
    getChartOpts(state: ChartState<any>, data: IData[][]) {
        const kpis = this.getKpis(state);

        const isValid = this.validateData(kpis.dim, kpis.metrics, data);
        if (!isValid) return this.getEmptyChart();

        return this.setChartOpts(kpis, this.cleanData(data!, kpis.dim, kpis.metrics));
    }

    protected setChartOpts(kpis: Kpis, data: IData[][]) {
        this.tempOpts = JSON.parse(JSON.stringify(lineChartOptions));
        this.setXaxis(data, kpis);
        this.setChartType(kpis.dim.type);
        this.setYaxisNames(kpis.metrics, yaxisOpts);
        this.setYaxisSeries(data, kpis);

        if (kpis.metrics.length === 1) {
            this.moveToolbarFor1Kpi();
        } else if (kpis.metrics.length === 2) {
            this.moveToolbarFor2Kpis();
        }

        return this.tempOpts;
    }

    private cleanData(data: IData[][], dim: Dimension, mets: Dimension[]) {
        let result = data;
        if (dim.value === 'weekday') result = this.sortDataIfWeekday(data);
        if (dim.type === 'bar') result = this.removeEmptySegments(mets, result);

        return result;
    }

    protected sortDataIfWeekday(data: IData[][]) {
        return data.map(arr => ArrayUtils.sortDataByWeekday(arr));
    }

    protected removeEmptySegments(mets: Dimension[], data: IData[][]) {
        return data.map(arr =>
            arr.reduce<IData[]>( // skip dates/categories with no data
                (acc, curr) =>
                    typeof curr[mets[0]?.value] === 'undefined' &&
                    typeof curr[mets[1]?.value] === 'undefined'
                        ? acc
                        : [...acc, curr],
                []
            )
        );
    }

    protected override setXaxis(data: IData[][], { dim }: { dim: Dimension }) {
        const field = dim.value;
        let categs = data.flatMap(arr => arr.map(item => item[field] as string));
        categs = Array.from(new Set(categs));

        const dateDims = [TimeFields.date, TimeFields.week, TimeFields.month];
        if (dateDims.includes(dim.value)) {
            categs = categs.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1));
        }

        this.tempOpts.xaxis = {
            ...this.tempOpts.xaxis,
            type: this.getXaxisType(dim),
            labels: {
                hideOverlappingLabels: !this.shouldBeWide(dim),
            },
            categories: categs,
        };
    }

    protected setYaxisSeries(data: IData[][], { dim, metrics }: Kpis) {
        const newSeries = data.flatMap(groupdData =>
            metrics.map(met => ({
                name: groupdData[0]['ig_account.ig_account'] as string,
                data: this.mapArrayFromRawData(groupdData, dim, met),
            }))
        );

        this.tempOpts.series = newSeries;
    }
}
